import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Truck, RotateCcw, Shield, ArrowRight, Crown } from 'lucide-react';
import SerenityProductCard from '../components/SerenityProductCard';
import { productsAPI, homepageAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const VibrantHome = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [sectionTitle, setSectionTitle] = useState('Explore Collections');
  const [sectionSubtitle, setSectionSubtitle] = useState('Discover our curated selection');
  const [bestSellersTitle, setBestSellersTitle] = useState('Best Sellers');
  const [bestSellersSubtitle, setBestSellersSubtitle] = useState('Most coveted pieces');
  const [bestSellersBadge, setBestSellersBadge] = useState('COLLECTOR\'S CHOICE');
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [homepageRes, categoriesRes] = await Promise.all([
          homepageAPI.getSettings(),
          homepageAPI.getCategories()
        ]);
        
        if (isMounted) {
          const activeSliders = (homepageRes.data?.sliders || []).filter(s => s.is_active !== false);
          setSliders(activeSliders.length > 0 ? activeSliders : DEFAULT_SLIDERS);
          
          const cats = categoriesRes.data?.categories || [];
          const visibleCats = cats.filter(c => c.is_visible !== false && (c.product_count > 0 || c.productCount > 0));
          setCategories(visibleCats);
          setSectionTitle(categoriesRes.data?.section_title || 'Explore Collections');
          setSectionSubtitle(categoriesRes.data?.section_subtitle || 'Discover our curated selection');
          
          setBestSellersTitle(homepageRes.data?.best_sellers_title || 'Best Sellers');
          setBestSellersSubtitle(homepageRes.data?.best_sellers_subtitle || 'Most coveted pieces');
          setBestSellersBadge(homepageRes.data?.best_sellers_badge || 'COLLECTOR\'S CHOICE');
          
          const featuredIds = homepageRes.data?.featured_product_ids || [];
          
          if (featuredIds.length > 0) {
            const featuredRes = await productsAPI.getByIds(featuredIds);
            if (isMounted) {
              setFeaturedProducts(featuredRes.data || []);
            }
          } else {
            const productsRes = await productsAPI.getAll({ is_best_seller: true, limit: 8 });
            if (isMounted) {
              setProducts(productsRes.data || []);
            }
          }
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError' && !error.cancelled) {
          console.error('Error fetching data:', error);
          setSliders(DEFAULT_SLIDERS);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  const bestSellers = featuredProducts.length > 0 
    ? featuredProducts.slice(0, 8)
    : products.filter(p => p.isBestSeller || p.is_best_seller).slice(0, 8);

  const DEFAULT_SLIDERS = [
    {
      id: 1,
      title: 'Luxury Collectibles',
      subtitle: 'Exquisite pieces for distinguished collectors',
      cta: 'Explore Collection',
      link: '/shop',
      image: 'https://images.unsplash.com/photo-1760890596502-d5a858c29e0e?w=1400',
      gradient: 'from-[#D4AF37] via-[#B8960C] to-[#D4AF37]',
    },
    {
      id: 2,
      title: 'Premium Quality',
      subtitle: 'Crafted with excellence and precision',
      cta: 'Shop Now',
      link: '/shop/category/sleep-calm',
      image: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=1400',
      gradient: 'from-[#D4AF37] via-[#B8960C] to-[#D4AF37]',
    },
    {
      id: 3,
      title: 'New Arrivals',
      subtitle: 'Discover our latest exclusive pieces',
      cta: 'View New',
      link: '/shop/category/relaxing-sound-instruments',
      image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=1400',
      gradient: 'from-[#D4AF37] via-[#B8960C] to-[#D4AF37]',
    }
  ];

  useEffect(() => {
    if (sliders.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliders.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [sliders.length]);

  const normalizeProduct = (product) => ({
    ...product,
    id: product._id || product.id,
    salePrice: product.sale_price || product.salePrice,
    inStock: product.in_stock !== undefined ? product.in_stock : (product.inStock !== undefined ? product.inStock : true),
    isNew: product.is_new || product.isNew,
    isBestSeller: product.is_best_seller || product.isBestSeller,
    reviewCount: product.review_count || product.reviewCount || 0,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-xl text-[#D4AF37] font-body">Loading luxury...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* HERO SLIDESHOW - Luxury Black & Gold */}
      <section className="relative h-[500px] sm:h-[600px] lg:h-[700px] overflow-hidden bg-[#0A0A0A]">
        {sliders.map((slide, index) => (
          <div
            key={slide.id || index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="relative h-full">
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              {/* Dark luxury overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              
              <div className="absolute inset-0 flex items-center">
                <div className="container mx-auto px-4">
                  <div className="max-w-3xl text-white">
                    <div className="inline-block px-6 py-3 bg-[#D4AF37]/20 backdrop-blur-sm rounded-full mb-6 border border-[#D4AF37]/50">
                      <span className="text-[#D4AF37] font-bold text-lg tracking-widest font-body flex items-center gap-2 uppercase">
                        <Crown className="h-5 w-5" />
                        {slide.badge_text || 'Exclusive'}
                      </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6 font-heading leading-none">
                      <span className="text-white">{slide.title?.split(' ')[0]} </span>
                      <span className="text-[#D4AF37]">{slide.title?.split(' ').slice(1).join(' ')}</span>
                    </h1>
                    <p className="text-lg sm:text-xl lg:text-2xl xl:text-3xl mb-6 sm:mb-10 font-body font-light text-[#E5E5E5]">
                      {slide.subtitle}
                    </p>
                    <Link to={slide.link}>
                      <Button 
                        size="lg" 
                        className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] px-8 sm:px-12 lg:px-16 py-4 sm:py-6 lg:py-8 text-base sm:text-lg lg:text-2xl font-bold font-body rounded-full shadow-2xl shadow-[#D4AF37]/30 hover:scale-105 transition-all"
                      >
                        {slide.cta_text || slide.cta || 'Shop Now'} <ArrowRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Controls */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCurrentSlide((prev) => (prev - 1 + sliders.length) % sliders.length)}
          className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/80 hover:bg-[#D4AF37] text-white hover:text-[#0A0A0A] rounded-full w-10 h-10 md:w-16 md:h-16 shadow-xl hidden sm:flex border border-[#D4AF37]/30"
        >
          <ChevronLeft className="h-5 w-5 md:h-8 md:w-8" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCurrentSlide((prev) => (prev + 1) % sliders.length)}
          className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/80 hover:bg-[#D4AF37] text-white hover:text-[#0A0A0A] rounded-full w-10 h-10 md:w-16 md:h-16 shadow-xl hidden sm:flex border border-[#D4AF37]/30"
        >
          <ChevronRight className="h-5 w-5 md:h-8 md:w-8" />
        </Button>

        {/* Slider dots */}
        <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 flex gap-2 md:gap-4">
          {sliders.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 md:h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-[#D4AF37] w-8 md:w-16' : 'bg-white/30 w-2 md:w-3 hover:bg-[#D4AF37]/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* CATEGORIES - Luxury Cards */}
      <section className="py-28 bg-gradient-to-b from-[#0A0A0A] via-[#141414] to-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <div className="inline-block px-6 py-2 bg-[#D4AF37]/10 rounded-full mb-6 border border-[#D4AF37]/30">
              <span className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase font-body">Collections</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-bold mb-6 font-heading text-white">{sectionTitle}</h2>
            <p className="text-xl lg:text-2xl text-[#888888] font-body">{sectionSubtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
            {categories.slice(0, 8).map((category, index) => {
              const productCount = category.product_count || category.productCount || 0;
              const displayName = category.display_name || category.name;
              
              const normalizedSlug = (category.slug || category.name || '')
                .toLowerCase()
                .replace(/&/g, '')
                .replace(/\s+/g, '-')
                .replace(/--+/g, '-');
              
              return (
                <Link
                  key={category._id || category.id}
                  to={`/shop/category/${normalizedSlug}`}
                  className="group"
                >
                  <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#1A1A1A] shadow-lg hover:shadow-2xl hover:shadow-[#D4AF37]/20 transition-all duration-500 hover:-translate-y-2 border border-[#D4AF37]/10 hover:border-[#D4AF37]/40">
                    <img
                      src={category.image}
                      alt={displayName}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {/* Dark gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
                    
                    {/* Category info */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="w-12 h-1 rounded-full bg-[#D4AF37] mb-4 group-hover:w-20 transition-all duration-300" />
                      <h3 className="text-xl lg:text-2xl font-bold mb-1 font-heading text-white">
                        {displayName}
                      </h3>
                      <p className="text-sm text-[#D4AF37] font-body">{productCount} items</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BEST SELLERS - Premium Section */}
      {bestSellers.length > 0 && (
        <section className="py-28 bg-[#0A0A0A]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-block px-8 py-4 bg-[#D4AF37] rounded-full mb-8 shadow-lg shadow-[#D4AF37]/30">
                <span className="text-[#0A0A0A] font-bold text-xl tracking-widest font-body uppercase">{bestSellersBadge}</span>
              </div>
              <h2 className="text-5xl lg:text-7xl font-bold text-white mb-6 font-heading">
                {bestSellersTitle}
              </h2>
              <p className="text-xl text-[#888888] font-body">
                {bestSellersSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {bestSellers.map((product) => (
                <SerenityProductCard key={product._id || product.id} product={normalizeProduct(product)} />
              ))}
            </div>

            <div className="text-center mt-16">
              <Link to="/shop?collection=best-sellers">
                <Button 
                  size="lg"
                  className="bg-transparent hover:bg-[#D4AF37] text-[#D4AF37] hover:text-[#0A0A0A] px-16 py-8 text-xl font-bold font-body rounded-full shadow-xl hover:scale-105 transition-all border-2 border-[#D4AF37]"
                >
                  View All Best Sellers <ArrowRight className="ml-3 h-6 w-6" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* USP BAR - Luxury Gold */}
      <section className="py-16 bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 mb-6 rounded-full bg-[#0A0A0A] flex items-center justify-center shadow-lg">
                <Truck className="h-12 w-12 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 font-heading text-[#0A0A0A]">Free Shipping</h3>
              <p className="text-lg text-[#0A0A0A]/70 font-body">On orders over $100</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-24 h-24 mb-6 rounded-full bg-[#0A0A0A] flex items-center justify-center shadow-lg">
                <RotateCcw className="h-12 w-12 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 font-heading text-[#0A0A0A]">30-Day Returns</h3>
              <p className="text-lg text-[#0A0A0A]/70 font-body">Hassle-free returns</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-24 h-24 mb-6 rounded-full bg-[#0A0A0A] flex items-center justify-center shadow-lg">
                <Shield className="h-12 w-12 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 font-heading text-[#0A0A0A]">Secure Payment</h3>
              <p className="text-lg text-[#0A0A0A]/70 font-body">100% secure checkout</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VibrantHome;
